import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs } from '@/components/ui';
import { canView } from '@/shared/lib/permissions';
import { useAuthStore } from '@/shared/stores/auth';
import { Agents } from './Agents';
import { Teams } from './Teams';
import { Roles } from './Roles';

/**
 * מסך אחד לניהול הצוות: נציגים / צוותים / תפקידים בלשוניות
 * (/admin/agents). הנתיבים הישנים /admin/teams ו-/admin/roles מפנים
 * לכאן עם הלשונית המתאימה. לשונית מוצגת רק למי שיש לו הרשאת צפייה
 * במסך המקורי (RBAC) — השרת אוכף ממילא על ה-API.
 */
const ALL_TABS = [
  { id: 'agents', label: 'נציגים', perm: 'agents' },
  { id: 'teams', label: 'צוותים', perm: 'teams' },
  { id: 'roles', label: 'תפקידים', perm: 'roles' },
];

/**
 * embedded = מוטמע כלשונית בתוך מסך אחר (הגדרות): הלשוניות הפנימיות נשמרות
 * ב-state ולא ב-?tab= — שכבר שייך למסך העוטף. initialTab מגיע מ-?sub=.
 */
export function StaffCenter({ embedded = false, initialTab }: { embedded?: boolean; initialTab?: string } = {}) {
  const [params, setParams] = useSearchParams();
  const [localTab, setLocalTab] = useState(initialTab ?? 'agents');
  const user = useAuthStore((s) => s.user);
  const tabs = ALL_TABS.filter((t) => canView(user, t.perm));
  const requested = embedded ? localTab : (params.get('tab') ?? 'agents');
  const tab = tabs.some((t) => t.id === requested) ? requested : (tabs[0]?.id ?? 'agents');
  const select = (id: string) => (embedded ? setLocalTab(id) : setParams(id === 'agents' ? {} : { tab: id }, { replace: true }));

  return (
    <div className="stack">
      {/* מוטמע בהגדרות: המסך העוטף כבר מרווח, אז בלי ריפוד כפול. */}
      <div style={embedded ? undefined : { padding: 'var(--gap) var(--gap) 0' }}>
        <Tabs
          items={tabs}
          value={tab}
          onChange={select}
        />
      </div>
      {tab === 'agents' && <Agents embedded={embedded} />}
      {tab === 'teams' && <Teams embedded={embedded} />}
      {tab === 'roles' && <Roles embedded={embedded} />}
    </div>
  );
}
